import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';

import { type Channel, type ChannelListResponse, SubscriptionSchema } from '../../../api';
import type { SubscriptionSummary } from '../../../api';
import { type ApiError, toApiError } from '../../../lib/api/api-client';
import { useApiClient } from '../../../lib/api/api-context';
import { useActionKeys } from '../../../lib/api/idempotency-key';

import type { ScreenFailure } from './screen-failure';

/** What {@link useChannelWrites} gives the route. */
export interface ChannelWrites {
  /** Channel ids with a write in flight (R4, AC35). */
  readonly busy: ReadonlySet<string>;
  /**
   * The server's answer of each write since the list in `base` arrived: a summary, or `null` once
   * removed. Empty as soon as a newer list replaces `base`: the new list is the server's truth.
   */
  readonly overlay: ReadonlyMap<string, SubscriptionSummary | null>;
  readonly writeFailure: ScreenFailure | null;
  readonly removed: string | null;
  /** Title of the channel whose POST last succeeded, cleared by the next tap (S9 R6, R21); E3 ignores it. */
  readonly added: string | null;
  readonly toggle: (channel: Channel) => void;
}

/** The answers kept, and the list they were made against. */
interface Overlay {
  readonly base: ChannelListResponse | undefined;
  readonly entries: ReadonlyMap<string, SubscriptionSummary | null>;
}

const NO_ENTRIES: ReadonlyMap<string, SubscriptionSummary | null> = new Map();

// A 409 on POST and a 404 on DELETE mean another device already did it (A-D4): reload, no error.
function changedElsewhere(error: ApiError, removing: boolean): boolean {
  return removing
    ? error.status === 404 && error.reasonCode === 'NOT_FOUND'
    : error.reasonCode === 'SUBSCRIPTION_ALREADY_EXISTS';
}

/**
 * Tick and untick on E3 (S5 R4): `POST /subscriptions` or `DELETE /subscriptions/{id}`, one
 * `Idempotency-Key` per action, kept for a retry only after a network failure, a 5xx or
 * `IDEMPOTENCY_KEY_IN_PROGRESS` (`useActionKeys`, S2 R13). A tap on a busy channel does nothing.
 * `base` is the list on screen; answers belong to it and are dropped when it changes.
 */
export function useChannelWrites({
  base,
  reload,
}: {
  readonly base: ChannelListResponse | undefined;
  readonly reload: () => void;
}): ChannelWrites {
  const api = useApiClient();
  const keys = useActionKeys('perAction');
  const inFlight = useRef(new Set<string>());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [kept, setKept] = useState<Overlay>({ base: undefined, entries: NO_ENTRIES });
  const [writeFailure, setWriteFailure] = useState<ScreenFailure | null>(null);
  const [removed, setRemoved] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  const setBusyFor = useCallback((id: string, on: boolean) => {
    if (on) inFlight.current.add(id);
    else inFlight.current.delete(id);
    setBusy(new Set(inFlight.current));
  }, []);

  const toggle = useCallback(
    (channel: Channel) => {
      if (inFlight.current.has(channel.id)) return; // AC35: one tap, one request
      const current = channel.subscription;
      const removing = current !== null;
      const action = removing ? `remove:${current.id}` : `select:${channel.id}`;
      const guard = { kind: 'authenticated', idempotencyKey: keys.keyFor(action) } as const;
      setBusyFor(channel.id, true);
      setWriteFailure(null);
      setRemoved(null);
      setAdded(null);
      const request: Promise<SubscriptionSummary | null> = removing
        ? api
            .delete(`/subscriptions/${encodeURIComponent(current.id)}`, { guard }, z.void())
            .then(() => null)
        : api
            .post('/subscriptions', { guard, body: { channelId: channel.id } }, SubscriptionSchema)
            .then((created) => ({
              id: created.id,
              status: created.status,
              suspensionReasonCode: created.suspensionReasonCode,
            }));
      request.then(
        (summary) => {
          keys.settle(action, null);
          setKept((previous) => ({
            base,
            entries: new Map(previous.base === base ? previous.entries : NO_ENTRIES).set(
              channel.id,
              summary,
            ),
          }));
          if (removing) setRemoved(channel.title);
          else setAdded(channel.title);
          setBusyFor(channel.id, false);
        },
        (error: unknown) => {
          const failure = toApiError(error);
          keys.settle(action, failure);
          setBusyFor(channel.id, false);
          if (changedElsewhere(failure, removing)) {
            reload();
            return;
          }
          setWriteFailure({ reasonCode: failure.reasonCode, params: failure.params });
        },
      );
    },
    [api, base, keys, reload, setBusyFor],
  );

  return {
    busy,
    overlay: kept.base === base ? kept.entries : NO_ENTRIES,
    writeFailure,
    removed,
    added,
    toggle,
  };
}
